import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import React from 'react';
import classnames from 'classnames';
import s from './RadioButton.scss';
/**
 * Contains the `checked` value of a radio button.
 *
 * Use this in the children of a headless {@link RadioButton} to react to the `checked` state.
 */ export const RadioButtonContext = /*#__PURE__*/ React.createContext(false);
/**
 * A headless Radio button that you can use to implement your own Radio buttons. This component exists to take the guesswork and frustration out of making an accessible implementation.
 *
 * It does this via requiring one of the following props: `hasAccessibleText`, `aria-label`, or `aria-labelledby`. Each prop has a description of when you should use it, so go check it out!
 *
 * BUG:
 * ---
 * If this RadioButton is included in a scrollable area, the scroll position will jump to the start of the scrollable area.
 * This is due to how the root label's `display: contents; position: relative;` interacts with  the hidden `<input>` tag's `position: absolute`
 * Because `position: relative` refuses to work with `display: contents`, the hidden input gets positioned in unexpected places.
 *
 * To work around this, you need to pass a `display: grid/flex/etc` style through the `className` prop.
 *
 * I've chosen not to mess with the `display: contents` rule inside this component's code since being headless a core part of its API.
 * TODO: Figure out a way to keep `display: contents` or similar while keeping the hidden input nearby
 */ const RadioButton = /*#__PURE__*/ React.forwardRef((_param, ref)=>{
    var { checked, children, 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledBy, className, onChange, // Just yanking this off so that we don't pass it through to the DOM
    hasAccessibleText } = _param, rest = _object_without_properties(_param, [
        "checked",
        "children",
        'aria-label',
        'aria-labelledby',
        "className",
        "onChange",
        "hasAccessibleText"
    ]);
    return /*#__PURE__*/ React.createElement(RadioButtonContext.Provider, {
        value: checked
    }, /*#__PURE__*/ React.createElement("label", {
        className: classnames(s['root'], className),
        "aria-label": ariaLabel,
        "aria-labelledby": ariaLabelledBy
    }, children, /*#__PURE__*/ React.createElement("input", _object_spread({
        ref: ref,
        type: "radio",
        className: s['input'],
        onChange: onChange,
        onKeyDown: (e)=>{
            if (e.key === 'Enter' || e.key === 'Space') {
                onChange && onChange(e);
            }
        },
        checked: checked
    }, rest))));
});
RadioButton.displayName = 'RadioButton';
export default RadioButton;
